/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from "@tanstack/react-query";
import { trackAssetFormOutcome } from "../../analytics";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import useAuthHttp from "../../hooks/useAuthHttp";
import { IAssetDetailsResponse, IAssetInfo, IAssetTypesResponse } from "../../models/asset";
import { useFormik } from "formik";
import * as Yup from "yup";
import View from "../Container";
import { groupAssetListTypeByGroupName } from "../../commons/group-assets-list-type";
import { ASSET_MIN_PURCHASE_DATE, yupAssetPurchaseDate } from "../../commons/assetPurchaseDate";
import { Toast } from "primereact/toast";
import Nav, { NavItem } from "../Nav";
import Text from "../Text";
import { useHomeContext } from "../../contexts/HomeContext";
import ButtonGroup from "../ButtonGroup";
import Button from "../Button";
import FormField from "../FormField";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import BankSearchSelector from "../BankSearchSelector";
import { InputNumber } from "primereact/inputnumber";
import Label from "../Label";
import Show from "../Show";
import MainLoader from "../../layout/MainLoader";
import Container from "../Container";
import { IExtractedInvestment } from "../../models/extractedData";
import { assetSourceFieldForDirpfImport } from "../../commons/dirpfImportMeta";

export interface ICustomizedHandleForm {
    type?: {
        id: string;
        name: string;
    };
    startDate?: Date;
    issuingBank?: {
        id: string;
        name: string;
    };
    operationCosts?: {
        brokerage?: number;
        others?: number;
    };
    amount?: number;
    description?: string;
}

export interface ICustomizedHandleFormProps {
    onSuccessRegister(assetInfo: ICustomizedHandleForm | null): void;
    goBack(): void;
    assetId?: string;
    initialData?: IExtractedInvestment;
}

const CustomizedHandleForm = (props: ICustomizedHandleFormProps) => {
    const http = useAuthHttp();
    const toast = useRef<Toast | null>(null);
    const homeContext = useHomeContext();
    const [isEdit, setIsEdit] = useState(false);

    const insertAssetMutation = useMutation({
        mutationFn: async (form: ICustomizedHandleForm) => {
            const body = {
                asset_id: form.type?.id,
                asset_type: "CS",
                asset_fin_issue_id: form.issuingBank?.id,
                asset_alias: form.description,
                asset_purchase_date: moment(form.startDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_event: {
                    event_type: "CHARGE",
                    event_date: moment(form.startDate).format("YYYY-MM-DD HH:mm:ss"),
                    event_value: form.amount
                },
                asset_cs_details: {
                    cs_oc_brokerage: form.operationCosts?.brokerage,
                    cs_oc_others: form.operationCosts?.others
                },
                ...(assetSourceFieldForDirpfImport(props.initialData) ?? {})
            };

            return await http.doPost<any, IAssetInfo>("/asset/insert", body);
        }
    });

    const updateAssetMutation = useMutation({
        mutationFn: async (form: ICustomizedHandleForm) => {
            const body = {
                asset_id: form.type?.id,
                asset_type: "CS",
                asset_fin_issue_id: form.issuingBank?.id,
                asset_alias: form.description,
                asset_purchase_date: moment(form.startDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_event: {
                    event_type: "CHARGE",
                    event_date: moment(form.startDate).format("YYYY-MM-DD HH:mm:ss"),
                    event_value: form.amount
                },
                asset_cs_details: {
                    cs_oc_brokerage: form.operationCosts?.brokerage,
                    cs_oc_others: form.operationCosts?.others
                }
            };

            return await http.doPatch<any, IAssetInfo>(`/asset/${props.assetId}`, body);
        }
    });

    const assetListTypeQuery = useQuery({
        queryKey: [`assetListType`],
        queryFn: async () =>
            await http.doGet<IAssetTypesResponse>('asset/list_types')
    });
    const assetListTypeGrouped = groupAssetListTypeByGroupName(assetListTypeQuery.data?.asset_list ?? []);

    const formik = useFormik<ICustomizedHandleForm>({
        initialValues: {
            type: undefined,
            startDate: undefined,
            issuingBank: undefined,
            operationCosts: {
                brokerage: 0,
                others: 0,
            },
            amount: 0,
            description: ""
        },
        validationSchema: Yup
            .object()
            .shape({
                type: Yup
                    .object()
                    .required("Campo obrigatório"),
                startDate: yupAssetPurchaseDate(),
                issuingBank: Yup
                    .object()
                    .nullable()
                    .optional(),
                operationCosts: Yup
                    .object()
                    .shape({
                        brokerage: Yup.string().optional(),
                        others: Yup.string().optional()
                    }),
                amount: Yup
                    .number()
                    .min(1, "É necessário informar um valor")
                    .required("Campo obrigatório"),
                description: Yup
                    .string()
                    .required("Campo obrigatório"),
            }),
        onSubmit: (form) => send(form)
    });

    const assetDetailsQuery = useQuery({
        queryKey: ["assetDetails", props.assetId],
        gcTime: 0,
        queryFn: async () => {
            if (!props.assetId) {
                setIsEdit(false);
                return {
                    asset_info: null
                };
            }

            setIsEdit(true);
            return await http.doGet<IAssetDetailsResponse>(`/asset/${props.assetId}`)
        }
    });

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    useEffect(() => {
        if (!assetDetailsQuery.data?.asset_info) {
            return;
        }

        const assetDetails = assetDetailsQuery.data;
        const type = assetListTypeGrouped
            .find(listType => listType.groupName == "Personalizado")
            ?.names
            .find(p => p.id == assetDetails.asset_info.asset_id);
        const eventUser = assetDetailsQuery.data?.asset_info.asset_event_user.find(a => a.event_type == "CHARGE");
        formik.setValues({
            type: type,
            startDate: moment(eventUser?.event_date).toDate(),
            issuingBank: {
                id: assetDetails.asset_info.asset_fin_issue?.id,
                name: assetDetails.asset_info.asset_fin_issue?.inst_name
            },
            operationCosts: {
                brokerage: assetDetails.asset_info.asset_cs_details?.cs_oc_brokerage ?? 0,
                others: assetDetails.asset_info.asset_cs_details?.cs_oc_others ?? 0
            },
            amount: assetDetails.asset_info.asset_current_value,
            description: assetDetails.asset_info.asset_alias
        });
    }, [assetDetailsQuery.data?.asset_info]);

    useEffect(() => {
        if (!props.initialData) {
            return;
        }
        const data = props.initialData;
        formik.setValues({
            startDate: data.asset_purchase_date ? moment(data.asset_purchase_date).toDate() : undefined,
            amount: data.asset_current_value || 0,
            description: data.asset_alias || "",
            issuingBank: data.asset_fin_issue ? {
                id: data.asset_fin_issue.id,
                name: data.asset_fin_issue.inst_name
            } : undefined
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialData]);

    const upsert = async (assetPayload: ICustomizedHandleForm) => {
        if (isEdit) return await updateAssetMutation.mutateAsync(assetPayload);

        return await insertAssetMutation.mutateAsync(assetPayload);
    }

    const send = async (form: ICustomizedHandleForm) => {
        if (!form) {
            return;
        }

        try {
            const assetInfo = await upsert(form);

            if (assetInfo) {
                props.onSuccessRegister(form);
                await homeContext.refresh();
            } else {
                if (!isEdit) trackAssetFormOutcome('customized', false, 'failed');
                toast.current?.show({
                    severity: "error",
                    summary: "Falha ao cadastrar investimento"
                });
            }
        } catch (err) {
            if (!isEdit) trackAssetFormOutcome('customized', false, 'failed');
            toast.current?.show({
                severity: "error",
                summary: (err as Error).message
            });
        }
    }

    return (
        <>
            <Toast ref={toast} />
            <Show when={assetDetailsQuery.isLoading}>
                <Container center fullWidth>
                    <MainLoader fullHeight={false} minHeight="300px" />
                </Container>
            </Show>

            <Show when={!assetDetailsQuery.isLoading}>
                <View flexColumn gap={3}>
                    <h2 className="m-0">Personalizado</h2>

                    <Nav>
                        <NavItem
                            id="go-back"
                            showPrefixIcon
                            onSelectOption={() => props.goBack()}
                        >
                            Voltar
                        </NavItem>
                    </Nav>

                    <View flexColumn>
                        <Text>
                            Escolha um tipo
                        </Text>

                        <View
                            className={`flex flex-row flex-wrap items-center mt-3 gap-2 rounded`}
                        >
                            <ButtonGroup
                                invalid={!!formik.errors.type && formik.touched.type}
                                group={assetListTypeGrouped
                                    .find(listType =>
                                        listType.groupName == "Personalizado"
                                    )?.names
                                    .map(g => (
                                        {
                                            name: g.name,
                                            disabled: isEdit,
                                            selected: formik.values.type?.id == g.id,
                                            onClick: () => formik.setFieldValue("type", g)
                                        })) ?? []
                                }
                            />
                        </View>

                        {
                            !!formik.errors.type && formik.touched.type && (
                                <Text className="text-red-500 mt-2" size="xs">{formik.errors.type}</Text>
                            )
                        }
                    </View>

                    <FormField
                        id="startDate"
                        label="Início"
                        hasError={!!formik.errors.startDate && formik.touched.startDate}
                        error={formik.errors.startDate}
                    >
                        <Calendar
                            invalid={!!formik.errors.startDate && formik.touched.startDate}
                            value={formik.values.startDate}
                            dateFormat="dd/mm/yy"
                            readOnlyInput
                            showIcon
                            minDate={ASSET_MIN_PURCHASE_DATE}
                            disabledDays={[0, 6]}
                            onChange={(event) => {
                                formik.setFieldValue("startDate", event.value)
                            }}
                            locale="pt"
                        />
                    </FormField>

                    <BankSearchSelector
                        hasError={!!formik.errors.issuingBank && !!formik.touched.issuingBank}
                        error={formik.errors.issuingBank}
                        selected={formik.values.issuingBank}
                        label="Qual é o banco emissor? (opcional)"
                        onSelectBank={(bank) => {
                            formik.setFieldValue("issuingBank", !bank ? null : { id: bank?._id, name: bank?.financial_inst_name });
                        }}
                    />

                    <View flexColumn gap={2}>
                        <Label>Corretagem</Label>
                        <InputNumber
                            value={formik.values.operationCosts?.brokerage}
                            onValueChange={(e) =>
                                formik.setFieldValue("operationCosts", { ...formik.values.operationCosts, brokerage: e.value })
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </View>

                    <View flexColumn gap={2}>
                        <Label>Outros custos</Label>
                        <InputNumber
                            value={formik.values.operationCosts?.others}
                            onValueChange={(e) =>
                                formik.setFieldValue("operationCosts", { ...formik.values.operationCosts, others: e.value })
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </View>

                    <FormField
                        id="amount"
                        hasError={!!formik.errors.amount && formik.touched.amount}
                        error={formik.errors.amount}
                        label="Valor investido"
                    >
                        <InputNumber
                            invalid={!!formik.errors.amount && formik.touched.amount}
                            value={formik.values.amount}
                            onValueChange={(e) =>
                                formik.setFieldValue("amount", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>

                    <FormField
                        id="description"
                        hasError={!!formik.errors.description && formik.touched.description}
                        error={formik.errors.description}
                        label="Nome"
                    >
                        <InputText
                            invalid={!!formik.errors.description && formik.touched.description}
                            value={formik.values.description}
                            onChange={(e) =>
                                formik.setFieldValue("description", e.target.value)
                            }
                        />
                    </FormField>

                    <View fullWidth>
                        <Button
                            onClick={() => {
                                formik.handleSubmit();
                                return Promise.resolve();
                            }}
                            className="w-full justify-content-center"
                            loading={insertAssetMutation.isPending || updateAssetMutation.isPending}
                            disabled={insertAssetMutation.isPending || updateAssetMutation.isPending}
                        >
                            {isEdit ? "Editar" : "Cadastrar"}
                        </Button>
                    </View>
                </View>
            </Show>
        </>
    )
}

export default CustomizedHandleForm;
